import React, { useState, useEffect, useCallback } from 'react';
import { RiSaveLine, RiToolsLine, RiGlobalLine, RiCheckLine, RiLockLine } from 'react-icons/ri';
import { adminGetConfig, adminUpdateConfig } from '../../api/admin.api';
import { C } from '../../data/constants';
import { PageHeader, LoadingScreen, Toast } from '../../components/shared/UI';

/* ══════════════════════════════════════════════════════════════
   SITE SETTINGS

   Publishing a JLPT level and taking a page down for maintenance used
   to be code changes. Both are decisions you make about a running
   product, so they belong here.

   Turning a page off is enforced server-side as well — the API returns
   503 for that page's routes, so a stale tab or a direct URL cannot
   walk around it.
══════════════════════════════════════════════════════════════ */

const LEVEL_BLURB = {
  N5: 'Beginner — kana, 800 words, basic grammar',
  N4: 'Elementary — everyday conversation & kanji',
  N3: 'Intermediate — natural spoken Japanese',
  N2: 'Upper-intermediate — newspapers & real texts',
  N1: 'Advanced — full academic & business fluency',
};

export default function SiteSettings() {
  const [config, setConfig] = useState(null);
  const [catalogue, setCatalogue] = useState({ levels: [], pages: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    adminGetConfig()
      .then(res => {
        setConfig({ levels: res.data.levels, pages: res.data.pages });
        setCatalogue(res.data.catalogue);
      })
      .catch(err => setError(err.message));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const res = await adminUpdateConfig(config);
      setConfig({ levels: res.data.levels, pages: res.data.pages });
      setNotice('Settings applied — live for every learner immediately.');
      setTimeout(() => setNotice(''), 3200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [config]);

  if (!config) return <LoadingScreen label="Loading site settings" />;

  const setLevel = (label, on) =>
    setConfig(c => ({ ...c, levels: { ...c.levels, [label]: on } }));

  const setPage = (key, patch) =>
    setConfig(c => ({ ...c, pages: { ...c.pages, [key]: { ...c.pages[key], ...patch } } }));

  const closedCount = Object.values(config.pages).filter(p => p.enabled === false).length;

  return (
    <div className="grm-admin anim-fade-up">
      <PageHeader
        title="Site Settings"
        sub={closedCount ? `${closedCount} page${closedCount === 1 ? '' : 's'} currently closed` : 'Everything is open'}
        action={(
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? <div className="spinner" /> : <><RiSaveLine /> Save settings</>}
          </button>
        )}
      />

      {error && <div className="grm-error">{error}</div>}

      {/* ── Levels ── */}
      <div className="ks-card grm-form" style={{ marginBottom: 18 }}>
        <div className="grm-form-section"><RiGlobalLine size={13} /> Live JLPT levels <span /></div>
        <p className="cfg-note">
          A level that is off stays visible in the picker but is marked &ldquo;not live yet&rdquo; and cannot be selected.
          Add its content first, then switch it on here — no redeploy needed.
        </p>

        <div className="cfg-levels">
          {catalogue.levels.map(label => {
            const on = config.levels[label] !== false;
            const locked = label === 'N5';
            return (
              <button
                key={label}
                type="button"
                className={`cfg-level${on ? ' on' : ''}${locked ? ' locked' : ''}`}
                style={{ '--lvl': C.accent }}
                disabled={locked}
                onClick={() => setLevel(label, !on)}
                title={locked ? 'N5 is the core course and cannot be switched off' : undefined}
              >
                <span className="cfg-level-chip">{label}</span>
                <span className="cfg-level-copy">
                  <strong>{on ? 'Live' : 'Hidden'}</strong>
                  <small>{LEVEL_BLURB[label]}</small>
                </span>
                {locked ? <RiLockLine size={14} /> : on ? <RiCheckLine size={16} /> : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Pages ── */}
      <div className="ks-card grm-form">
        <div className="grm-form-section"><RiToolsLine size={13} /> Page availability <span /></div>
        <p className="cfg-note">
          Closing a page leaves it in the menu but shows a maintenance notice instead of its content.
          The API for that page returns 503 too, so a direct link or an open tab cannot get around it.
        </p>

        <div className="cfg-pages">
          {catalogue.pages.map(({ key, label, route }) => {
            const page = config.pages[key] || { enabled: true, message: '' };
            const on = page.enabled !== false;
            return (
              <div key={key} className={`cfg-page${on ? '' : ' closed'}`}>
                <div className="cfg-page-head">
                  <div className="cfg-page-id">
                    <strong>{label}</strong>
                    <code>{route}</code>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    className={`cfg-switch${on ? ' on' : ''}`}
                    onClick={() => setPage(key, { enabled: !on })}
                  >
                    <span className="cfg-switch-knob" />
                    <em>{on ? 'Open' : 'Closed'}</em>
                  </button>
                </div>

                {!on && (
                  <input
                    className="ks-input"
                    value={page.message || ''}
                    maxLength={300}
                    placeholder="Message shown to learners (optional) — e.g. Back on Monday."
                    onChange={e => setPage(key, { message: e.target.value })}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {notice && <Toast message={notice} />}
    </div>
  );
}
